import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { labelWithSpecies } from 'common/utils/species'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import PermissionGuard from 'common/guards/PermissionGuard'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { isSameDay } from 'date-fns'
import { useIsShrimpSpecie, useSpeciesName } from 'modules/pond/api/selector'
import { CycleHistory } from 'modules/pond/api/types'
import MoreAction from 'modules/pond/components/MoreAction'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
// นำเข้า Icon สำหรับปุ่ม Power
import { Box, Button, Checkbox, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import CycleExportBar from './CycleExportBar'

type SettingTableProps = {
  cycles: CycleHistory[]
  isLoading: boolean
  onEdit: (id: string) => void
  onEnd: (id: string) => void
  onDelete: (id: string) => void
  showUpdateCycleBtn: boolean
  showDeleteCycleBtn: boolean
}

const SettingTable = ({
  cycles,
  isLoading,
  onEdit,
  onEnd,
  onDelete,
  showUpdateCycleBtn,
  showDeleteCycleBtn,
}: SettingTableProps) => {
  const { t } = useTranslation(['pondComparison', 'pond', 'food', 'common'])
  const label = useDefaultValueLabel()
  const theme = useTheme()
  const speciesName = useSpeciesName()
  const isShrimpSpecies = useIsShrimpSpecie()

  // 1. ค้นหารอบการเลี้ยงที่ยังทำงานอยู่ (Active) เพื่อนำ ID ไปใช้กับปุ่มด้านบน
  const activeCycle = cycles.find((cycle) => cycle.status !== 'completed')

  // Selection is only for exporting, so it lives here rather than in a shared store:
  // nothing outside this table needs to know which cycles are ticked.
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const isAllSelected = cycles.length > 0 && selectedIds.length === cycles.length

  const toggleSelecting = () => {
    setIsSelecting((selecting) => !selecting)
    // Leaving selection mode clears the ticks, so re-entering never starts with a
    // stale selection the user cannot see.
    setSelectedIds([])
  }

  const toggleCycle = (id: string) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((el) => el !== id) : [...ids, id]))
  }

  const toggleAll = () => {
    setSelectedIds(isAllSelected ? [] : cycles.map((cycle) => cycle.id))
  }

  // Export order follows the table, not the order the user happened to tick.
  const selectedCycleIds = cycles.map((cycle) => cycle.id).filter((id) => selectedIds.includes(id))

  const selectColumn = {
    id: 'select',
    title: (
      <Checkbox
        checked={isAllSelected}
        indeterminate={selectedIds.length > 0 && !isAllSelected}
        onChange={toggleAll}
        inputProps={{ 'aria-label': t('pond:select.all') }}
      />
    ),
    // style.css indents the first column by 50px, and the checkbox itself is 40px, so
    // anything narrower than 90px clips it.
    width: '100px',
    preventRowClick: true,
  }

  const columns = [
    ...(isSelecting ? [selectColumn] : []),
    {
      id: 'datetime',
      title: t('pondComparison:cycleLabel'),
      width: '250px',
    },
    {
      id: 'shrimpAge',
      title: (
        <>
          <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />
        </>
      ),
      width: '100px',
    },
    {
      id: 'shrimpYield',
      title: (
        <>
          {t('pond:total.production')} ({t('food:kilo')})
        </>
      ),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'fcr',
      title: t('pond:fcr'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'survivalRate',
      title: t('pond:survive.rate'),
      width: '150px',
    },
    {
      id: 'initialShrimpCount',
      title: `${t('pond:initial.shrimp.count', { species: speciesName })}`,
      width: '150px',
      format: fNumber,
    },
    {
      id: 'breedBrand',
      title: labelWithSpecies(t('pond:breed'), speciesName),
      width: '150px',
    },
    {
      id: 'shrimpBrand',
      title: labelWithSpecies(t('pond:breed.brand'), speciesName),
      width: '150px',
    },
    {
      id: 'breedProduct',
      title: isShrimpSpecies ? labelWithSpecies(t('pond:breed.product'), speciesName) : t('pond:initial.size'),
      width: '150px',
    },
    {
      id: 'salinity',
      title: t('pond:saltiness'),
      width: '150px',
      format: fNumber,
    },
    {
      id: 'feedProgram',
      title: isShrimpSpecies ? t('pond:program') : t('food:consuming.food.chart.title.short'),
      width: '150px',
    },
    {
      id: 'status',
      title: t('common:status'),
      width: '150px',
    },
    {
      id: 'action',
      title: '',
      width: '100px',
      preventRowClick: true,
    },
  ]

  const createTableRows = (
    id: string,
    datetime: string,
    shrimpAge: number,
    shrimpYield: number | null,
    fcr: number | null,
    survivalRate: number | null,
    status: string,
    initialShrimpCount: number,
    initialSize: number | undefined,
    salinity: number,
    breedBrand: string,
    shrimpBrand: string,
    breedProduct: string,
    feedProgram: string,
    feedPercentOfFishWeight: number
  ) => ({
    id,
    select: (
      <Checkbox
        checked={selectedIds.includes(id)}
        onChange={() => toggleCycle(id)}
        inputProps={{ 'aria-label': datetime }}
      />
    ),
    datetime,
    shrimpAge,
    shrimpYield,
    fcr,
    survivalRate: <Typography>{survivalRate === null ? '-' : `${fNumber(survivalRate)}%`}</Typography>,
    initialShrimpCount: fNumber(initialShrimpCount),
    initialSize: initialSize ? fNumber(initialSize) : '-',
    salinity: fNumber(salinity),
    breedBrand,
    shrimpBrand,
    breedProduct,
    feedProgram: isShrimpSpecies
      ? feedProgram && feedProgram.trim() !== ''
        ? feedProgram
        : '-'
      : feedPercentOfFishWeight
      ? fNumber(feedPercentOfFishWeight)
      : '-',
    status: (
      <Stack direction="row" alignItems="center" spacing={1}>
        <FiberManualRecordIcon
          sx={{
            color: status == 'active' ? theme.palette.success.main : theme.palette.grey[400],
            fontSize: 14,
            ml: 0.5,
          }}
        />
        <Typography variant="body2" fontWeight={600}>
          {status == 'active' ? t('pond:pond.active') : t('pond:end.pond.cycle')}
        </Typography>
      </Stack>
    ),
    action: (
      <MoreAction
        featureKey={FeatureKey.Cycle}
        isHideOnCompleted={false}
        onClickOptions={[
          ...(showUpdateCycleBtn
            ? [
                {
                  icon: <EditIcon />,
                  text: t('pond:edit'),
                  action: () => {
                    onEdit(id)
                  },
                  crudIdx: CRUDAction.Update,
                },
              ]
            : []),
          ...(showDeleteCycleBtn
            ? [
                {
                  icon: <DeleteIcon />,
                  text: t('pond:delete.pond.cycle'),
                  action: () => {
                    onDelete(id)
                  },
                  crudIdx: CRUDAction.Delete,
                },
              ]
            : []),
        ]}
      />
    ),
  })

  const dashboardDateDisplayValue = (startDate: Date | null, endDate: Date | null) => {
    if (!startDate) return ''
    if (!endDate || isSameDay(endDate, new Date())) {
      return `${formatThaiShortDateTwoDigitYear(startDate)} - ${t('pond:present')}`
    }
    return `${formatThaiShortDateTwoDigitYear(startDate)} - ${formatThaiShortDateTwoDigitYear(
      endDate
    )}`
  }

  const rows = cycles.map((el) =>
    createTableRows(
      el.id,
      dashboardDateDisplayValue(
        el.startDate ? new Date(el.startDate) : null,
        el.endDate ? new Date(el.endDate) : null
      ),
      el.shrimpAge,
      // DOF-1357: null renders as "–" in the table
      el.yieldEstimatable === false ? null : el.yield,
      el.yieldEstimatable === false ? null : el.fcr,
      el.yieldEstimatable === false ? null : el.survivalRate,
      el.status,
      el.initialShrimpCount,
      el.initialSize,
      el.salinity,
      label.breed(el.breed.name, el.breed.id),
      el.shrimpBrand.brand,
      isShrimpSpecies
        ? label.breedProduct(el.breedProduct.productName)
        : el.initialSize
        ? fNumber(el.initialSize)
        : '-',
      el.feedProgram.name,
      el.feedPercentOfFishWeight
    )
  )

  return (
    <>
      <BaseTableCard
        title={t('pond:cycle.history')}
        refresh={() => {}}
        isLoading={isLoading}
        isEmpty={cycles.length === 0}
        titleColor={theme.palette.primary.dark}
        disableShowDateFilter={true}
        disableRefresh={true}
        action={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {cycles.length > 0 && (
              <PermissionGuard featureKeys={[FeatureKey.PdfSummary]}>
                <Button
                  variant="outlined"
                  color={isSelecting ? 'inherit' : 'primary'}
                  startIcon={isSelecting ? <CloseIcon /> : <CheckBoxOutlinedIcon />}
                  onClick={toggleSelecting}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    boxShadow: 'none',
                  }}
                >
                  {isSelecting ? t('pond:cancel.selection') : t('pond:select.cycle')}
                </Button>
              </PermissionGuard>
            )}
            {activeCycle && showUpdateCycleBtn && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PowerSettingsNewIcon />}
                onClick={() => onEnd(activeCycle.id)}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  boxShadow: 'none',
                }}
              >
                {t('pond:end.pond.cycle')}
              </Button>
            )}
          </Stack>
        }
      >
        <Box className="setting-table">
          <BasicTable
            columns={columns}
            onLoading={() => {}}
            isLoading={isLoading}
            rows={rows}
            onRowClick={showUpdateCycleBtn ? (row) => onEdit(row.id) : undefined}
          />
        </Box>
      </BaseTableCard>

      {isSelecting && <CycleExportBar selectedCycleIds={selectedCycleIds} />}
    </>
  )
}

export default SettingTable
